// Excel 자동 시트 선택을 위한 유틸리티 함수들

import { ExcelX2File, ExcelX2FileSheet } from '../models/excelModel/excelX2';
import {
  ServiceType,
  SERVICE_REQUIRED_SHEETS,
  SHEET_KEYWORDS,
  EXCLUSION_KEYWORDS,
  SERVICE_NAMES,
  NotificationType,
  SheetSelectionResult,
  NotificationResult,
  RequiredSheetType
} from '../constants/ServiceSheetMapping';

/**
 * 시트명과 키워드를 매칭하는 함수 (우선순위 기반)
 * @param sheetName 시트명
 * @param targetKeywords 매칭할 키워드 배열
 * @param exclusionKeywords 제외할 키워드 배열 (선택)
 * @returns 매칭 우선순위 (0: 매칭 안됨, 1: 오염된 매칭, 2: 순수 매칭, 3: 정확한 매칭)
 */
export const findMatchingSheet = (
  sheetName: string,
  targetKeywords: readonly string[],
  exclusionKeywords: readonly string[] = []
): number => {
  const normalizedSheetName = sheetName.toLowerCase().trim();

  // '증감'이 포함된 시트는 제외
  if (normalizedSheetName.includes('증감')) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚫 '증감' 포함으로 제외됨: ${sheetName}`);
    }
    return 0;
  }

  let hasKeywordMatch = false;
  let hasExactMatch = false;

  // 1단계: 정확한 매칭 확인
  for (const keyword of targetKeywords) {
    if (normalizedSheetName === keyword.toLowerCase()) {
      hasExactMatch = true;
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ 정확한 매칭 (우선순위 3): ${sheetName} === ${keyword}`);
      }
      break;
    }
  }

  if (hasExactMatch) {
    return 3; // 최우선순위: 정확한 매칭
  }

  // 2단계: 포함 매칭 확인
  for (const keyword of targetKeywords) {
    if (normalizedSheetName.includes(keyword.toLowerCase())) {
      hasKeywordMatch = true;
      break;
    }
  }

  if (!hasKeywordMatch) {
    return 0; // 매칭 안됨
  }

  // 3단계: 제외 키워드 확인
  let hasExclusionKeyword = false;
  for (const exclusionKeyword of exclusionKeywords) {
    if (normalizedSheetName.includes(exclusionKeyword.toLowerCase())) {
      hasExclusionKeyword = true;
      if (process.env.NODE_ENV === 'development') {
        console.log(`⚠️ 제외 키워드 발견: ${sheetName} includes ${exclusionKeyword}`);
      }
      break;
    }
  }

  if (hasExclusionKeyword) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`🟡 오염된 매칭 (우선순위 1): ${sheetName}`);
    }
    return 1; // 낮은 우선순위: 오염된 포함 매칭
  }

  if (process.env.NODE_ENV === 'development') {
    console.log(`✅ 순수 매칭 (우선순위 2): ${sheetName}`);
  }
  return 2; // 중간 우선순위: 순수 포함 매칭
};

/**
 * 특정 필수 시트 타입에 해당하는 시트를 찾는 함수
 * @param sheetName 시트명
 * @param requiredSheetType 필수 시트 타입 ('원가', '집계', '내역')
 * @returns 매칭 우선순위 (0: 매칭 안됨, 1: 오염된 매칭, 2: 순수 매칭, 3: 정확한 매칭)
 */
export const matchesRequiredSheetType = (sheetName: string, requiredSheetType: RequiredSheetType): number => {
  const keywords = SHEET_KEYWORDS[requiredSheetType];
  const exclusionKeywords = EXCLUSION_KEYWORDS[requiredSheetType] || [];
  return findMatchingSheet(sheetName, keywords, exclusionKeywords);
};

/**
 * 파일 정보에서 자동으로 시트를 선택하는 메인 함수
 * ⚠️ 개선사항: 우선순위 기반 시트 선택 (정확한 매칭 > 순수 매칭 > 오염된 매칭)
 * @param fileInfos 파일 정보 배열
 * @param serviceType 서비스 타입
 * @returns 업데이트된 파일 정보와 선택 결과
 */
export const autoSelectSheets = (
  fileInfos: ExcelX2File[],
  serviceType: ServiceType
): {
  updatedFileInfos: ExcelX2File[];
  selectionResult: SheetSelectionResult;
} => {
  const requiredSheets = SERVICE_REQUIRED_SHEETS[serviceType];
  const selectedSheets: string[] = [];
  const foundSheetTypes = new Set<RequiredSheetType>();

  // 각 파일의 시트들을 순회하면서 자동 선택
  const updatedFileInfos = fileInfos.map(fileInfo => {
    const updatedSheets = fileInfo.sheets?.map(sheet => ({ ...sheet, checked: false }));
    const selectedSheetNames = new Set<string>(); // 이미 선택된 시트 추적

    // 각 필요 시트 타입별로 가장 높은 우선순위의 시트 선택
    requiredSheets.forEach(requiredSheet => {
      const requiredSheetType = requiredSheet as RequiredSheetType;

      let bestSheet: ExcelX2FileSheet | null = null;
      let bestPriority = 0;

      // 모든 시트를 순회하며 가장 높은 우선순위의 시트를 찾기
      for (const sheet of updatedSheets || []) {
        if (selectedSheetNames.has(sheet.name)) {
          continue; // 이미 선택된 시트는 건너뛰기
        }

        if (sheet.isHidden) {
          continue; // 숨겨진 시트는 자동선택 대상에서 제외
        }

        const priority = matchesRequiredSheetType(sheet.name, requiredSheetType);

        if (priority > bestPriority) {
          bestSheet = sheet;
          bestPriority = priority;
        }
      }

      // 가장 높은 우선순위의 시트 선택
      if (bestSheet && bestPriority > 0) {
        bestSheet.checked = true;
        selectedSheets.push(bestSheet.name);
        selectedSheetNames.add(bestSheet.name);
        foundSheetTypes.add(requiredSheetType);
      }
    });

    // 파일의 전체 선택 상태 업데이트
    const checkedSheets = updatedSheets?.filter(sheet => sheet.checked) || [];
    const visibleSheets = updatedSheets?.filter(sheet => !sheet.isHidden) || [];
    const isEntireChecked = visibleSheets.length > 0 && checkedSheets.length === visibleSheets.length;

    return {
      ...fileInfo,
      sheets: updatedSheets,
      isEntireChecked
    };
  });

  // 누락된 시트 확인
  const missingSheets: string[] = [];
  for (const requiredSheet of requiredSheets) {
    if (!foundSheetTypes.has(requiredSheet as RequiredSheetType)) {
      missingSheets.push(requiredSheet);
    }
  }

  return {
    updatedFileInfos,
    selectionResult: {
      selectedSheets: selectedSheets, // 중복이 이미 방지되므로 Set 처리 불필요
      missingSheets,
      requiredSheets: [...requiredSheets]
    }
  };
};

/**
 * 선택 결과를 바탕으로 알림 메시지를 생성하는 함수
 * @param serviceType 서비스 타입
 * @param result 시트 선택 결과
 * @returns 알림 타입과 메시지
 */
export const generateNotificationMessage = (
  serviceType: ServiceType,
  result: SheetSelectionResult
): NotificationResult => {
  const serviceName = SERVICE_NAMES[serviceType];
  const { selectedSheets, missingSheets, requiredSheets } = result;
  
  // 아무것도 선택되지 않은 경우
  if (selectedSheets.length === 0) {
    return {
      type: NotificationType.NO_SELECTION,
      message: `${serviceName}에 필요한 시트를 찾을 수 없습니다.\n` +
               `필요한 시트: ${requiredSheets.join(', ')}\n` +
               `수동으로 선택해주세요.`
    };
  }
  
  // 모든 필수 시트가 선택된 경우
  if (missingSheets.length === 0) {
    return {
      type: NotificationType.COMPLETE_SUCCESS,
      message: `${serviceName}에 필요한 시트가 자동선택되었습니다.\n` +
               `선택된 시트: ${selectedSheets.join(', ')}`
    };
  }
  
  // 일부만 선택된 경우
  return {
    type: NotificationType.PARTIAL_SUCCESS,
    message: `${serviceName}에 일부 시트만 자동선택되었습니다.\n` +
             `선택된 시트: ${selectedSheets.join(', ')}\n` +
             `누락된 시트: ${missingSheets.join(', ')} (수동으로 선택해주세요)`
  };
};

/**
 * 모든 파일의 시트 선택을 초기화하는 함수
 * @param fileInfos 파일 정보 배열
 * @returns 초기화된 파일 정보 배열
 */
export const clearAllSheetSelections = (fileInfos: ExcelX2File[]): ExcelX2File[] => {
  return fileInfos.map(fileInfo => ({
    ...fileInfo,
    isEntireChecked: false,
    sheets: fileInfo.sheets?.map(sheet => ({
      ...sheet,
      checked: false
    }))
  }));
};


/**
 * 선택된 시트들의 셀 합계를 계산하는 함수
 * @param fileInfos 파일 정보 배열
 * @returns 셀 합계
 */
export const calculateSelectedSheetCells = (fileInfos: ExcelX2File[]): number => {
  let totalCells = 0;
  
  fileInfos.forEach(fileInfo => {
    fileInfo.sheets?.forEach(sheet => {
      if (sheet.checked) {
        totalCells += sheet.lastRow * sheet.lastColumn;
      }
    });
  });
  
  return totalCells;
};

/**
 * 디버그용: 자동 선택 과정을 로그로 출력하는 함수
 * @param serviceType 서비스 타입
 * @param fileInfos 파일 정보
 * @param result 선택 결과
 */
export const logAutoSelectionProcess = (
  serviceType: ServiceType,
  fileInfos: ExcelX2File[],
  result: SheetSelectionResult
): void => {
  if (process.env.NODE_ENV === 'development') {
    console.group(`🔍 자동 시트 선택 - ${SERVICE_NAMES[serviceType]}`);
    console.log('📋 필요한 시트:', SERVICE_REQUIRED_SHEETS[serviceType]);
    console.log('📁 업로드된 파일들:');
    
    fileInfos.forEach((file, index) => {
      console.log(`  ${index + 1}. ${file.name}`);
      console.log('    시트들:', file.sheets?.map(s => `${s.name}${s.checked ? ' ✅' : ''}`).join(', '));
    });
    
    console.log('✅ 선택된 시트:', result.selectedSheets);
    console.log('❌ 누락된 시트:', result.missingSheets);
    console.groupEnd();
  }
};